import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight, Plus, BookOpen, Star, Flame } from 'lucide-react';
import { motion } from 'framer-motion';
import { fmtPrice } from '@/lib/format';
import { SafeImage } from '@/components/SafeImage';
import { useCart } from '@/contexts/CartContext';
import { PUBLIC_MENU_ENABLED } from '@/lib/launchMode';
import { toast } from 'sonner';

/**
 * FeaturedCarousel
 * Premium responsive carousel for SmithyBrowns signature dishes.
 *
 * Props:
 *   - items: array of featured menu items (with featured_description, image_url, sizes/price, tags)
 *   - autoplay (default true)
 *
 * Designed to be reused for: customer favourites, most-ordered, seasonal specials,
 * limited-time dishes, promotional offers — just pass the items and a label override.
 */
export const FeaturedCarousel = ({ items = [], autoplay = true, label = 'Signature', headline = 'FEATURED DISHES', subheading = 'Discover the dishes our customers keep coming back for.' }) => {
  const plugins = autoplay ? [Autoplay({ delay: 5500, stopOnInteraction: true, stopOnMouseEnter: true })] : [];
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', skipSnaps: false, dragFree: false }, plugins);
  const [selected, setSelected] = useState(0);
  const [snaps, setSnaps] = useState([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi?.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    setSnaps(emblaApi.scrollSnapList());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', () => setSnaps(emblaApi.scrollSnapList()));
    onSelect();
  }, [emblaApi]);

  const { addItem } = useCart();
  const handleAdd = (item) => {
    const size = item.has_sizes ? item.sizes?.[0]?.name : null;
    addItem(item, size, 1);
    toast.success(`Added ${item.name} to cart`);
  };

  if (!items.length) return null;

  return (
    <section data-testid="featured-dishes-carousel" className="relative py-12 sm:py-16 overflow-hidden">
      {/* Backdrop accents */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[hsl(var(--sb-gold))]/10 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-[hsl(var(--sb-green))]/10 blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 text-[hsl(var(--sb-green))] uppercase text-xs tracking-widest"><Flame size={12} /> {label}</div>
            <h2 className="font-display text-4xl sm:text-5xl text-white mt-1">{headline}</h2>
            <p className="text-white/70 text-sm sm:text-base mt-2 max-w-lg">{subheading}</p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={scrollPrev}
              aria-label="Previous"
              data-testid="featured-carousel-prev"
              className="h-11 w-11 rounded-full border border-[hsl(var(--sb-border))] text-white hover:border-[hsl(var(--sb-gold))] hover:text-[hsl(var(--sb-gold))] transition-colors grid place-items-center"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={scrollNext}
              aria-label="Next"
              data-testid="featured-carousel-next"
              className="h-11 w-11 rounded-full bg-[hsl(var(--sb-gold))] text-black hover:bg-[hsl(var(--sb-gold-2))] transition-colors grid place-items-center"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Embla viewport */}
        <div className="overflow-hidden -mx-2" ref={emblaRef}>
          <div className="flex touch-pan-y">
            {items.map((item) => (
              <div key={item.id} className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-2">
                <FeaturedCard item={item} onAdd={handleAdd} />
              </div>
            ))}
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {snaps.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${i === selected ? 'w-8 bg-[hsl(var(--sb-gold))]' : 'w-2 bg-white/25 hover:bg-white/45'}`}
            />
          ))}
        </div>

        {/* Mobile arrows under dots */}
        <div className="sm:hidden flex items-center justify-center gap-3 mt-3">
          <button onClick={scrollPrev} aria-label="Previous" className="h-10 w-10 rounded-full border border-[hsl(var(--sb-border))] text-white grid place-items-center"><ChevronLeft size={18} /></button>
          <button onClick={scrollNext} aria-label="Next" className="h-10 w-10 rounded-full bg-[hsl(var(--sb-gold))] text-black grid place-items-center"><ChevronRight size={18} /></button>
        </div>
      </div>
    </section>
  );
};

const FeaturedCard = ({ item, onAdd, categoryName }) => {
  const startingPrice = item.has_sizes
    ? (item.sizes && item.sizes.length ? Math.min(...item.sizes.map((s) => s.price)) : null)
    : item.price;
  const desc = item.featured_description || item.description;
  const isHalal = (item.tags || []).includes('halal');
  return (
    <motion.article
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      data-testid="featured-dish-card"
      className="sb-card overflow-hidden h-full flex flex-col group hover:border-[hsl(var(--sb-gold))]/40 transition-colors"
    >
      <div className="relative aspect-[4/3] bg-black overflow-hidden">
        <SafeImage
          src={item.image_url}
          alt={item.name}
          className="absolute inset-0 w-full h-full"
          imgClassName="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.06] transition-transform duration-500"
          fallbackIconSize={40}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

        {/* Signature indicator — simple orange flame, no yellow pill / no "SIGNATURE" text */}
        <div
          title="Signature dish"
          aria-label="Signature dish"
          data-testid="featured-dish-signature-badge"
          className="absolute top-3 left-3 inline-flex items-center justify-center h-8 w-8 rounded-full bg-black/45 backdrop-blur-sm ring-1 ring-orange-500/50 shadow-lg shadow-black/50"
        >
          <Flame size={18} className="text-orange-400" fill="currentColor" />
        </div>
        {isHalal && (
          <div
            title="Halal certified"
            aria-label="Halal certified"
            data-testid="featured-dish-halal-badge"
            className="absolute top-3 right-3 inline-flex items-center justify-center h-8 w-8 rounded-full bg-[hsl(var(--sb-green))] text-black text-[10px] font-black leading-none shadow-lg shadow-black/50 ring-1 ring-black/20"
            style={{ fontFamily: '"Noto Sans Arabic", "Segoe UI", Tahoma, sans-serif' }}
          >
            حلال
          </div>
        )}

        {/* Title overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h3 className="font-display text-2xl sm:text-3xl text-white leading-none drop-shadow-lg">{item.name.toUpperCase()}</h3>
        </div>
      </div>

      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        <p className="text-sm text-white/75 line-clamp-2">{desc}</p>

        <div className="flex items-end justify-between mt-4">
          {startingPrice != null ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/45">Starting from</div>
              <div className="font-display text-2xl text-[hsl(var(--sb-gold))]">{fmtPrice(startingPrice)}</div>
            </div>
          ) : <div />}
          {categoryName && (
            <span className="text-[10px] uppercase tracking-widest text-white/55 border border-[hsl(var(--sb-border))] rounded-full px-2 py-1">{categoryName}</span>
          )}
        </div>

        {PUBLIC_MENU_ENABLED && (
          <div className="mt-4">
            <Link
              to="/menu"
              data-testid="featured-view-menu"
              className="w-full h-11 inline-flex items-center justify-center gap-1.5 px-4 rounded-md bg-[hsl(var(--sb-gold))] text-black font-semibold hover:bg-[hsl(var(--sb-gold-2))] transition-colors"
            >
              <BookOpen size={15} /> View on Menu
            </Link>
          </div>
        )}
      </div>
    </motion.article>
  );
};

export default FeaturedCarousel;
